"use client";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/solid";
import LogoSvg from "@/components/svg/logo";
import LanguageSwitcher from "./ui/switcher";
import RentModal from "./rent-modal";
import PurchaseModal from "./purchase-modal";
import Link from "next/link";
import { useTranslations, useLocale } from "next-intl";
import { routing } from "@/i18n/routing";

interface MenuItem {
  name: string;
  href: string;
}

export default function BurgerMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [showBurger, setShowBurger] = useState(true);
  const [isScrollingUp, setIsScrollingUp] = useState(true);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const t = useTranslations("menu");
  const locale = useLocale();

  const closeMenu = () => {
    setIsOpen(false);
    setTimeout(() => {
      setShowBurger(true);
      document.body.classList.remove("overflow-hidden");
    }, 500); // Delay to match the animation duration
  };

  const toggleMenu = () => {
    if (isOpen) {
      closeMenu();
    } else {
      setShowBurger(false);
      setIsOpen(true);
      document.body.classList.add("overflow-hidden");
    }
  };

  // localePrefix: "as-needed" (routing.ts) → дефолтная локаль (en) живёт на "/" БЕЗ
  // префикса, а ru/th — на "/ru", "/th". Поэтому base для en должен быть "" (страница
  // на /), а для остальных — "/{locale}". Раньше было `/${locale}#...` всегда → для en
  // получался /en#about, которого НЕТ: next редиректит /en → / и ТЕРЯЕТ якорь → клик по
  // меню не скроллил (на дефолтной локали, т.е. на большинстве заходов). Вот и «не
  // работает нигде». Для ru/th работало, т.к. /ru#about — валидный путь.
  const base = locale === routing.defaultLocale ? "" : `/${locale}`;
  const menuItems: MenuItem[] = [
    { name: t("about"), href: `${base}/#about` },
    { name: t("buildingPlans"), href: `${base}/#flat-for-sale` },
    { name: t("gallery"), href: `${base}/#gallery` },
    { name: t("contacts"), href: `${base}/#contacts` },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      if (scrollTop > lastScrollTop) {
        // Scrolling down
        setIsScrollingUp(false);
      } else {
        // Scrolling up
        setIsScrollingUp(true);
      }
      setLastScrollTop(scrollTop <= 0 ? 0 : scrollTop); // For mobile or negative scrolling
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollTop]);


  return (
    <div className="relative">
      {/* Navigation */}
      {!isOpen && showBurger && (
        <motion.div
          className={`max-w-[1200px] mx-auto px-4 fixed top-0 left-0 right-0 z-50 transition-transform duration-300 ${
            isScrollingUp ? "translate-y-0" : "-translate-y-full"
          }`}
          initial={{ opacity: 0 }} // Start invisible
          animate={{ opacity: 1 }} // Fade in
          exit={{ opacity: 0 }} // Fade out
          transition={{ duration: 0.6, ease: "easeInOut" }}
        >
          <nav className="flex bg-[#F7F8F8] p-4 mt-[5px] text-white justify-between items-center rounded-full h-[55px]">
            {/* Logo */}
            <div>
              <Link href={`${base}/`} aria-label={t("navHome")}>
                <LogoSvg />
              </Link>
            </div>

            {/* Desktop Menu — отдельный <nav> с уникальным aria-label (у бургер-меню
                свой), чтобы два меню не считались дублирующими ссылками. */}
            {/* gap-x-2 между ссылками + padding px-3 py-2 внутри каждой = кликзоны ≥24px
                по высоте И ≥24px расстояние между соседями (WCAG 2.2 target-size). Раньше
                ссылки были голым текстом ~15px без зазоров кликзон → FAIL. Внешний вид
                почти не меняется (текст 12px, чуть больше воздуха). */}
            <nav
              aria-label={t("navPrimary")}
              className="lg:flex gap-x-2 text-black hidden text-[12px] items-center"
            >
              {menuItems.map((item, i) => (
                <Link
                  key={i}
                  href={item.href}
                  className="cursor-pointer inline-flex items-center min-h-6 px-3 py-2"
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA: «Купить квартиру» (главная цель сайта) — доминирующая яркая
                кнопка; «Аренда» — зеркало той же формы, вторичный цвет. Обе variant="header".
                Старая кнопка «Связаться» (BlueButton, tel:) убрана — телефон доступен в
                бургере и футере, а в шапке чистая пара Купить/Аренда без разнобоя. */}
            <div className="hidden md:flex items-center space-x-3">
              <LanguageSwitcher />
              <PurchaseModal variant="header" />
              <RentModal variant="header" />
            </div>

            {/* Mobile version: «Купить» + «Аренда» + бургер (по требованию — обе CTA
                видны в шапке). Купить — яркий главный CTA, Аренда — вторичная зеркальная,
                обе variant="header" (компактная форма). Телефон доступен в бургере/футере. */}
            <div className="flex md:hidden items-center space-x-2">
              <PurchaseModal variant="header" />
              <RentModal variant="header" />

              <motion.button
                aria-label={isOpen ? t("menuClose") : t("menuOpen")}
                aria-expanded={isOpen}
                className="z-50 p-2 text-white bg-[#04BBDC] rounded-full bg-no-repeat bg-bottom"
                style={{
                  backgroundImage: !isOpen
                    ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='43' height='29' viewBox='0 0 43 29'%3E%3Cpath d='M0.955778 15.2655C0.781494 16.18 0.772866 17.109 0.929895 18.0255L0.955777 18.1758L1.34972 19.5543L1.87493 20.7287C2.39747 21.6768 3.09342 22.5612 3.94126 23.3544L4.04162 23.4483C4.86798 24.3187 5.86645 25.0819 7.0001 25.7094L7.96598 26.2442C8.7628 26.6853 9.5961 27.0853 10.4909 27.3923C11.5781 27.7654 13.1965 28.2669 14.8201 28.5725C17.6564 29.1063 21.6444 31.4678 24.5 31.0001C27.3556 30.5323 31.6804 30.0513 34.0001 28.6728C36.3198 27.2943 38.8728 27.3938 42.0001 23.9999C43.7214 22.1317 56.6094 14.8859 56.9999 14.2983C58.406 12.1826 52.5 3.39089 52.5 -4.12313e-07C51.3546 1.81215 32.8601 9.3147 30.2758 9.11815L28.5313 8.98541L17.5011 7.64512L16.2573 7.55578L14.8129 7.50475L13.6931 7.55578L12.4456 7.64512L11.2638 7.76001L10.4102 7.86213L9.42542 8.0281L8.57188 8.25139C8.00471 8.3999 7.462 8.59939 6.95466 8.84603L6.93046 8.85775C6.31854 9.14664 5.73511 9.47065 5.1852 9.82713L4.8295 10.0576L4.17289 10.5171L3.51635 11.0788L3.40053 11.1788C3.08424 11.4521 2.79377 11.7429 2.53153 12.0488L2.48269 12.112C1.73354 13.0831 1.21615 14.1514 0.955778 15.2655Z' fill='%2306ABC8'/%3E%3C/svg%3E")`
                    : "",
                  width: "48px",
                  height: "48px",
                }}
                onClick={toggleMenu}
                whileTap={{ scale: 0.9 }}
              >
                <AnimatePresence mode="wait">
                  {isOpen ? (
                    <motion.div
                      key="close"
                      initial={{ opacity: 0, rotate: 0 }}
                      animate={{ opacity: 1, rotate: 360 }}
                      exit={{ opacity: 0, rotate: -360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <XMarkIcon className="w-8 h-8 text-white" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="open"
                      initial={{ opacity: 0, rotate: -180 }}
                      animate={{ opacity: 1, rotate: 0 }}
                      exit={{ opacity: 0, rotate: -180 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Bars3Icon className="w-8 h-8 text-white" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>
          </nav>
        </motion.div>
      )}

      {/* Full-screen animation */}
      <motion.div
        initial="closed"
        animate={isOpen ? "open" : "closed"}
        variants={{
          closed: {
            clipPath: "circle(30px at 95% 40px)",
            opacity: 0.5,
            transition: {
              duration: 0.4,
              ease: "easeInOut",
            },
          },
          open: {
            clipPath: "circle(1000px at 95% 40px)", // уменьшаем радиус для мобильных
            opacity: 1,
            transition: {
              duration: 0.8,
              ease: "easeInOut",
            },
          },
        }}
        className={`fixed top-0 right-0 bottom-0 left-0 z-40 bg-[#04BBDC] ${
          isOpen ? "visible" : "invisible"
        }`}
        style={{
          backgroundImage: `url('/back_menu.avif')`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "bottom center",
          backgroundSize: "contain",
          willChange: "clip-path", // сохраняем оптимизацию
        }}
      ></motion.div>

<motion.div
  initial={false}
  animate={isOpen ? "open" : "closed"}
  variants={{
    closed: {
      clipPath: "circle(30px at 95% 40px)",
      opacity: 0.5,
      transition: {
        duration: 0.6,
        ease: "easeInOut",
      },
    },
    open: {
      clipPath: "circle(1500px at 95% 40px)", // Full-screen coverage
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeInOut",
      },
    },
  }}
  className={`fixed top-0 right-0 bottom-0 left-0 z-40 bg-[#04BBDC] transition-all duration-500 ease-in-out ${
    isOpen ? "visible" : "invisible"
  }`}
  style={{
    backgroundImage: `url('/back_menu.avif')`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "bottom center",
    backgroundSize: "contain",
  }}
>
  <div className="container">
    <nav className="flex p-4 mt-[10px] text-white items-center rounded-full h-[55px] pl-5 justify-end">
      <motion.button
        aria-label={isOpen ? t("menuClose") : t("menuOpen")}
        aria-expanded={isOpen}
        className="z-50 p-2 text-white bg-[#04BBDC] rounded-full"
        style={{
          width: "48px",
          height: "48px",
        }}
        onClick={toggleMenu}
        whileTap={{ scale: 0.9 }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ opacity: 0, rotate: 0 }}
              animate={{ opacity: 1, rotate: 360 }}
              exit={{ opacity: 0, rotate: -360 }}
              transition={{ duration: 0.5 }}
            >
              <XMarkIcon className="w-8 h-8 text-white" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ opacity: 0, rotate: -180 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: -180 }}
              transition={{ duration: 0.5 }}
            >
              <Bars3Icon className="w-8 h-8 text-white" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </nav>
  </div>
  <motion.div
    className="p-10 text-white flex flex-col justify-between h-[90%]"
    initial={{ opacity: 0, translateY: 20 }}
    animate={{ opacity: isOpen ? 1 : 0, translateY: isOpen ? 0 : 20 }}
    transition={{ delay: isOpen ? 0.8 : 0, duration: 0.5, ease: "easeOut" }}
  >
    {/* Навигация по разделам. Раньше была aria-hidden/role=presentation — из-за этого
        на мобиле навигация по якорям была недоступна скринридерам (десктопное меню на
        мобиле hidden, а это скрыто → пользователь клавиатуры/скринридера оставался вообще
        без навигации). Убрано: делаем полноценный <nav>. Дубля с десктопным меню нет —
        то hidden ниже lg, это только мобильное, одновременно виден лишь один. Якоря
        реальные: #about (server), #flat-for-sale/#gallery/#contacts на обёртках
        DeferHydrate — все есть в DOM всегда, даже до ленивого монтажа секции.
        onClick=closeMenu ТОЧЕЧНО на каждом пункте (а не toggleMenu на всей обёртке —
        тот перехватывал клики и вместе с battle href='ru#about' делал пункты
        «некликабельными»): пункт и навигирует по якорю, и закрывает меню. */}
    <nav aria-label={t("navSections")} className="space-y-5 flex flex-col">
      {menuItems.map((item, i) => (
        <motion.div
          key={item.href}
          initial={{ opacity: 0, translateY: 10 }}
          animate={{ opacity: isOpen ? 1 : 0, translateY: isOpen ? 0 : 10 }}
          transition={{
            delay: isOpen ? 0.8 + i * 0.1 : 0,
            duration: 0.2,
            ease: "easeOut",
          }}
        >
          <Link href={item.href} onClick={closeMenu} className="cursor-pointer inline-block py-1">
            {item.name}
          </Link>
        </motion.div>
      ))}
    </nav>

    {/* Contact info and button */}
    <motion.div
      className="flex flex-col items-start space-y-[25px] mt-10"
      initial={{ opacity: 0, translateY: 10 }}
      animate={{ opacity: isOpen ? 1 : 0, translateY: isOpen ? 0 : 10 }}
      transition={{ delay: isOpen ? 1 : 0, duration: 0.3, ease: "easeOut" }}
    >
      <LanguageSwitcher />
      <Link href={"tel:+66930011220"}>
        <div>+ 66 93 001 1220</div>
      </Link>
      <Link href="mailto:sales@whalemarinacondo.com">
        <div>sales@whalemarinacondo.com</div>
      </Link>
      {/* Купить (главный CTA) и Аренда — ОДИНАКОВАЯ форма (variant="menu": обе во всю
          ширину, одинаковый размер/радиус), различаются только цветом: Купить залита
          ярким акцентом, Аренда — вторичная. Никакого разнобоя. */}
      <div className="w-full sm:w-[315px]">
        <PurchaseModal variant="menu" />
      </div>
      <div className="w-full sm:w-[315px]">
        <RentModal variant="menu" />
      </div>
      <Link
        href={"https://maps.app.goo.gl/DPgfghhjuiRTXmsMA"}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full sm:w-[315px]"
      >
        <button className="bg-[#005F70] text-white py-[18px] px-4 rounded-[5px] text-sm w-full">
          {t("map_contact")}
        </button>
      </Link>
    </motion.div>
  </motion.div>
</motion.div>
    </div>
  );
}
